import React, { useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { useLanguage } from '../../../../contexts/LanguageContext';

export const CATEGORY_LEVELS = {
    MAIN: 'MAIN',
    SECONDARY: 'SECONDARY',
    SUB: 'SUB',
    SUB_PART: 'SUB_PART',
    FAILURE_MODES: 'FAILURE'
};

const MainCategoryTable = ({ items, loading, lazyParams = { page: 1, rows: 10, searchText: '' }, totalRecords, onPage, handleEdit, categoryLevel }) => {
    const { th } = useLanguage();
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const ENGLISH_LANGUAGE_ID = 1;
    const TURKISH_LANGUAGE_ID = 2;

    const handleSort = (event) => {
        setSortField(event.sortField);
        setSortOrder(event.sortOrder);
    };

    const sortedItems = useMemo(() => {
        if (!items || !sortField) return items;

        return [...items].sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (sortField === 'turkishName') {
                aValue = a.translations?.find((t) => t.languageId === TURKISH_LANGUAGE_ID)?.text || '';
                bValue = b.translations?.find((t) => t.languageId === TURKISH_LANGUAGE_ID)?.text || '';
            } else if (sortField === 'englishName') {
                aValue = a.translations?.find((t) => t.languageId === ENGLISH_LANGUAGE_ID)?.text || '';
                bValue = b.translations?.find((t) => t.languageId === ENGLISH_LANGUAGE_ID)?.text || '';
            }

            aValue = String(aValue || '').toLowerCase();
            bValue = String(bValue || '').toLowerCase();

            if (aValue < bValue) return sortOrder === 1 ? -1 : 1;
            if (aValue > bValue) return sortOrder === 1 ? 1 : -1;
            return 0;
        });
    }, [items, sortField, sortOrder]);

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')} onClick={() => handleEdit(rowData)} />
            </div>
        );
    };

    const turkishNameBodyTemplate = (rowData) => {
        if (rowData && rowData.translations && Array.isArray(rowData.translations)) {
            const turkishTranslation = rowData.translations.find((t) => t.languageId === TURKISH_LANGUAGE_ID);
            if (turkishTranslation) {
                return <span className="font-medium text-900">{turkishTranslation.text || '-'}</span>;
            }
        }
        return <span className="text-600 italic">{th('faultCodes.common.translationNotFound')}</span>;
    };

    const englishNameBodyTemplate = (rowData) => {
        if (rowData && rowData.translations && Array.isArray(rowData.translations)) {
            const englishTranslation = rowData.translations.find((t) => t.languageId === ENGLISH_LANGUAGE_ID);
            if (englishTranslation) {
                return <span className="font-medium text-900">{englishTranslation.text || '-'}</span>;
            }
        }
        return <span className="text-600 italic">{th('faultCodes.common.translationNotFound')}</span>;
    };

    const statusBodyTemplate = (rowData) => {
        const statusValue = rowData.isActive;
        let severity;
        let displayValue;
        let icon;

        if (statusValue === 1) {
            severity = 'success';
            displayValue = th('common.active');
            icon = 'pi pi-check-circle';
        } else if (statusValue === 2) {
            severity = 'danger';
            displayValue = th('common.deleted');
            icon = 'pi pi-trash';
        } else if (statusValue === 0) {
            severity = 'warning';
            displayValue = th('common.inactive');
            icon = 'pi pi-times-circle';
        } else {
            severity = 'info';
            displayValue = th('common.unknown');
            icon = 'pi pi-question-circle';
        }
        return (
            <div className="flex justify-content-center">
                <Tag value={displayValue} severity={severity} icon={icon} className="px-3 py-1 font-medium" />
            </div>
        );
    };

    const mainCategoryNameTemplate = (rowData) => {
        return <span className="font-medium text-900">{rowData.mainCategoryName || '-'}</span>;
    };

    const secondCategoryNameTemplate = (rowData) => {
        return <span className="font-medium text-900">{rowData.secondaryCategoryName || '-'}</span>;
    };

    const subCategoryNameTemplate = (rowData) => {
        return <span className="font-medium text-900">{rowData.subCategoryName || '-'}</span>;
    };

    const getEmptyMessage = () => {
        const emptyMessages = {
            [CATEGORY_LEVELS.MAIN]: {
                icon: 'pi-folder',
                title: th('faultCodes.mainCategoryNotFound'),
                subtitle: th('faultCodes.mainCategoryEmpty')
            },
            [CATEGORY_LEVELS.SECONDARY]: {
                icon: 'pi-folder-open',
                title: th('faultCodes.secondCategoryNotFound'),
                subtitle: th('faultCodes.secondCategoryEmpty')
            },
            [CATEGORY_LEVELS.SUB]: {
                icon: 'pi-file',
                title: th('faultCodes.subCategoryNotFound'),
                subtitle: th('faultCodes.subCategoryEmpty')
            },
            [CATEGORY_LEVELS.SUB_PART]: {
                icon: 'pi-sitemap',
                title: th('faultCodes.subPartCategoryNotFound'),
                subtitle: th('faultCodes.subPartCategoryEmpty')
            },
            [CATEGORY_LEVELS.FAILURE_MODES]: {
                icon: 'pi-exclamation-triangle',
                title: th('faultCodes.failureModeNotFound'),
                subtitle: th('faultCodes.failureModeEmpty')
            }
        };

        const message = emptyMessages[categoryLevel] || {
            icon: 'pi-inbox',
            title: th('common.noRecordsFound'),
            subtitle: th('common.noRecordsToDisplay')
        };

        return (
            <div className="flex flex-column align-items-center justify-content-center py-6">
                <i className={`pi ${message.icon} text-6xl text-400 mb-4`}></i>
                <h3 className="text-600 font-semibold mb-2 text-xl">{message.title}</h3>
                <p className="text-500 text-center max-w-20rem">{message.subtitle}</p>
            </div>
        );
    };

    const firstEntry = lazyParams && typeof lazyParams.page === 'number' && typeof lazyParams.rows === 'number' && lazyParams.rows > 0 ? (lazyParams.page - 1) * lazyParams.rows : 0;

    return (
        <>
            <Tooltip target=".custom-tooltip" />
            <DataTable
                value={sortedItems}
                lazy
                paginator
                first={firstEntry}
                rows={lazyParams.rows}
                totalRecords={totalRecords}
                onPage={onPage}
                onSort={handleSort}
                sortField={sortField}
                sortOrder={sortOrder}
                loading={loading}
                emptyMessage={getEmptyMessage()}
                dataKey="id"
                responsiveLayout="scroll"
                stripedRows
                rowHover
                pageLinkSize={3}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="{first} - {last} / {totalRecords}"
                rowsPerPageOptions={[5, 10, 25, 50]}
                loadingIcon="pi pi-spin pi-spinner"
                size="small"
                showGridlines
            >
                <Column body={actionBodyTemplate} exportable={false} style={{ textAlign: 'center', width: '120px' }} frozen />
                <Column
                    field="englishName"
                    header={
                        <div className="flex align-items-center">
                            <i className="pi pi-language mr-2"></i>
                            <span>{th('faultCodes.englishDescription')}</span>
                        </div>
                    }
                    body={englishNameBodyTemplate}
                    sortable
                    style={{ minWidth: '280px' }}
                />
                <Column
                    field="turkishName"
                    header={
                        <div className="flex align-items-center">
                            <i className="pi pi-flag mr-2"></i>
                            <span>{th('faultCodes.turkishDescription')}</span>
                        </div>
                    }
                    body={turkishNameBodyTemplate}
                    sortable
                    style={{ minWidth: '280px' }}
                />
                {(categoryLevel === CATEGORY_LEVELS.SECONDARY || categoryLevel === CATEGORY_LEVELS.SUB || categoryLevel === CATEGORY_LEVELS.SUB_PART) && (
                    <Column
                        field="mainCategoryName"
                        header={
                            <div className="flex align-items-center">
                                <i className="pi pi-folder mr-2"></i>
                                <span>{th('faultCodes.mainCategory')}</span>
                            </div>
                        }
                        body={mainCategoryNameTemplate}
                        sortable
                        style={{ minWidth: '220px' }}
                    />
                )}
                {(categoryLevel === CATEGORY_LEVELS.SUB || categoryLevel === CATEGORY_LEVELS.SUB_PART) && (
                    <Column
                        field="secondaryCategoryName"
                        header={
                            <div className="flex align-items-center">
                                <i className="pi pi-folder-open mr-2"></i>
                                <span>{th('faultCodes.secondCategory')}</span>
                            </div>
                        }
                        body={secondCategoryNameTemplate}
                        sortable
                        style={{ minWidth: '220px' }}
                    />
                )}
                {categoryLevel === CATEGORY_LEVELS.SUB_PART && (
                    <Column
                        field="subCategoryName"
                        header={
                            <div className="flex align-items-center">
                                <i className="pi pi-file mr-2"></i>
                                <span>{th('faultCodes.subCategory')}</span>
                            </div>
                        }
                        body={subCategoryNameTemplate}
                        sortable
                        style={{ minWidth: '220px' }}
                    />
                )}
                <Column
                    field="isActive"
                    header={
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-info-circle mr-2"></i>
                            <span>{th('common.status')}</span>
                        </div>
                    }
                    body={statusBodyTemplate}
                    sortable
                    style={{ minWidth: '160px', textAlign: 'center' }}
                    align="center"
                />
            </DataTable>
        </>
    );
};

export default MainCategoryTable;